import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { QUEBEC } from "@/lib/quebec";
import { useLang } from "@/lib/i18n";

const PINS = [
  { id: "manawan", name: "Manawan", xy: QUEBEC.pins.manawan, color: "#E11B22" },
  { id: "ndg", name: "NDG", xy: QUEBEC.pins.ndg, color: "#0057B7" },
];

const MAP_BG =
  "https://images.pexels.com/photos/15835031/pexels-photo-15835031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PIN_PATH =
  "M0 0 C -14 -28, -30 -42, -30 -64 A 30 30 0 1 1 30 -64 C 30 -42 14 -28 0 0 Z";

export default function ImpactMap() {
  const [active, setActive] = useState("manawan");
  const { t } = useLang();

  return (
    <section
      id="map"
      data-testid="map-section"
      className="relative border-t border-white/10 py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0">
        <img src={MAP_BG} alt="" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#0B0C10]/90" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="mb-14 max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#4d9fff]">
            {t.map_label}
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
            {t.map_title}
          </h2>
          <p className="mt-5 text-sm sm:text-lg text-zinc-300">
            {t.map_sub}
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-8 items-center">
          {/* Map */}
          <Reveal
            className="relative rounded-sm border border-white/10 bg-[#12131C] p-4 md:p-8"
            data-testid="quebec-map"
          >
            <div
              className="absolute inset-0 rounded-sm opacity-40 blur-2xl"
              style={{ background: "radial-gradient(circle at 30% 88%, rgba(0,87,183,0.35), transparent 55%)" }}
            />
            <svg
              viewBox={QUEBEC.viewBox}
              className="relative h-[400px] sm:h-[520px] w-auto max-w-full mx-auto block"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <linearGradient id="qcStroke" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#0057B7" />
                  <stop offset="100%" stopColor="#E11B22" />
                </linearGradient>
                <filter id="qcGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="5" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              <motion.path
                d={QUEBEC.path}
                fill="rgba(0,87,183,0.06)"
                stroke="url(#qcStroke)"
                strokeWidth="4"
                strokeLinejoin="round"
                filter="url(#qcGlow)"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 2.6, ease: "easeInOut" }}
              />

              {PINS.map((p) => {
                const [x, y] = p.xy;
                const isActive = active === p.id;
                return (
                  <g
                    key={p.id}
                    transform={`translate(${x} ${y}) scale(${isActive ? 1.12 : 1})`}
                    onMouseEnter={() => setActive(p.id)}
                    onClick={() => setActive(p.id)}
                    className="cursor-pointer"
                    style={{ transition: "transform 0.3s ease" }}
                    data-testid={`map-pin-${p.id}`}
                  >
                    {/* beating pulse ring */}
                    <circle cx="0" cy="-64" r="30" fill="none" stroke={p.color} strokeWidth="3">
                      <animate attributeName="r" from="26" to="70" dur="1.9s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.8" to="0" dur="1.9s" repeatCount="indefinite" />
                    </circle>
                    {/* pin body */}
                    <path
                      d={PIN_PATH}
                      fill={p.color}
                      stroke="#ffffff"
                      strokeWidth={isActive ? 3 : 0}
                      style={{ filter: `drop-shadow(0 6px 10px ${p.color}80)` }}
                    />
                    <circle cx="0" cy="-64" r="12" fill="#0B0C10" />
                    <circle cx="0" cy="-64" r="5" fill="#ffffff" />
                    <text x="42" y="-52" fill="#ffffff" fontSize="42" className="font-heading" style={{ fontWeight: 600 }}>
                      {p.name}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Reveal>

          {/* Tooltip data logs */}
          <div className="space-y-4" data-testid="map-logs">
            {PINS.map((p) => {
              const isActive = active === p.id;
              return (
                <button
                  key={p.id}
                  onMouseEnter={() => setActive(p.id)}
                  onClick={() => setActive(p.id)}
                  data-testid={`map-log-${p.id}`}
                  className={`w-full text-left rounded-sm border p-6 transition-all duration-300 ${
                    isActive
                      ? p.color === "#0057B7"
                        ? "border-[#0057B7]/60 bg-[#12131C] glow-blue"
                        : "border-[#E11B22]/60 bg-[#12131C] glow-red"
                      : "border-white/10 bg-[#0d0e14] hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5" style={{ color: p.color }} />
                    <h3 className="font-heading text-xl font-medium text-white">{p.name}</h3>
                  </div>
                  <p className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">
                    {t.map_pins[p.id].region}
                  </p>
                </button>
              );
            })}
            <p className="pt-2 text-sm text-zinc-400">
              {t.map_more}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
